import React, { useState, useEffect, useRef } from 'react';
import './Genius.css';
import lyricsData from '../data/needYaLyrics.json';

interface Annotation {
  id: string;
  user: string;
  text: string;
  timestamp: number;
}

interface LyricLine {
  id: number;
  text: string;
  annotations: Annotation[];
}

interface GeniusProps {
  currentTrackIndex?: number;
  isPlaying?: boolean;
  onPlayTrack?: (trackIndex: number) => void;
  onTogglePlay?: () => void;
}

// Neon colors for user handles (same as Guestbook)
const NEON_COLORS = [
  '#FF25F8', // Hot pink
  '#00B8D4', // Darker cyan
  '#00C853', // Darker green
  '#FF073A', // Neon red
  '#F57C00', // Dark orange
  '#7C4DFF', // Purple
  '#FFD600', // Gold
  '#00E676', // Bright green
];

const getUserColor = (username: string): string => {
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  return NEON_COLORS[Math.abs(hash) % NEON_COLORS.length];
};

const Genius: React.FC<GeniusProps> = ({
  currentTrackIndex = -1,
  isPlaying = false,
  onPlayTrack = () => {},
  onTogglePlay = () => {}
}) => {
  const [lines] = useState<LyricLine[]>(lyricsData.lines);
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const [annotationOffset, setAnnotationOffset] = useState<number>(0);
  const [showWelcome, setShowWelcome] = useState<boolean>(true);
  const lineRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  // Track index for "need ya" is 4 (from the tracks array)
  const NEED_YA_TRACK_INDEX = 4;
  const isNeedYaPlaying = currentTrackIndex === NEED_YA_TRACK_INDEX && isPlaying;

  const handlePlayButtonClick = () => {
    if (currentTrackIndex === NEED_YA_TRACK_INDEX) {
      // If "need ya" is the current track, toggle play/pause
      onTogglePlay();
    } else {
      // Otherwise, switch to "need ya"
      onPlayTrack(NEED_YA_TRACK_INDEX);
    }
  };

  const handleLineClick = (lineId: number, hasAnnotations: boolean) => {
    if (hasAnnotations) {
      setSelectedLineId(selectedLineId === lineId ? null : lineId);

      // Calculate the offset for the annotation relative to the content area
      if (selectedLineId !== lineId && lineRefs.current[lineId]) {
        const lineElement = lineRefs.current[lineId];
        if (lineElement) {
          // Get the line's position relative to the lyrics panel, adjusted up by 50px
          const offset = lineElement.offsetTop + 24 - 50; // Adding padding, subtracting 50px to position higher
          setAnnotationOffset(offset);
        }
      }
    }
  };

  const selectedLine = lines.find(line => line.id === selectedLineId);

  return (
    <div className="genius">
      {showWelcome && (
        <div className="genius-welcome-overlay" onClick={() => setShowWelcome(false)}>
          <div className="genius-welcome-modal" onClick={(e) => e.stopPropagation()}>
            <div className="genius-welcome-text">
              Welcome to Genius. Tap on a lyric to read annotations.
            </div>
            <button className="genius-welcome-dismiss" onClick={() => setShowWelcome(false)}>
              Got it
            </button>
          </div>
        </div>
      )}

      <div className="genius-header">
        <div className="genius-header-top">
          <h1 className="genius-title">{lyricsData.song}</h1>
          {isNeedYaPlaying ? (
            <div className="genius-playing-animation">
              <div className="genius-bar genius-bar1"></div>
              <div className="genius-bar genius-bar2"></div>
              <div className="genius-bar genius-bar3"></div>
            </div>
          ) : (
            <button className="genius-play-button" onClick={handlePlayButtonClick}>
              Play song
            </button>
          )}
        </div>
        <p className="genius-artist">{lyricsData.artist}</p>
      </div>

      <div className="genius-content">
        {/* Lyrics Panel */}
        <div className="genius-lyrics-panel">
          {lines.map((line) => {
            const hasAnnotations = line.annotations.length > 0;
            const isSelected = selectedLineId === line.id;

            return (
              <div
                key={line.id}
                ref={(el) => { lineRefs.current[line.id] = el; }}
                className="genius-line-wrapper"
              >
                <span
                  className={`genius-line ${hasAnnotations ? 'has-annotation' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleLineClick(line.id, hasAnnotations)}
                >
                  {line.text || '\u00A0'}
                </span>
              </div>
            );
          })}
        </div>

        {/* Annotations Panel */}
        {selectedLine && selectedLine.annotations.length > 0 && (
          <div className="genius-annotations-panel">
            <div className="genius-annotations" style={{ marginTop: `${annotationOffset}px` }}>
              <div className="genius-annotation-list">
                {selectedLine.annotations.map((annotation) => (
                  <div key={annotation.id} className="genius-annotation">
                    <div className="genius-annotation-user">
                      <span
                        className="genius-user-handle"
                        style={{ color: getUserColor(annotation.user) }}
                      >
                        @{annotation.user}
                      </span>
                    </div>
                    <div className="genius-annotation-text">
                      {annotation.text}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Genius;
